import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, certificateUrl, certificatePdfUrl, type MyCertificate } from '../api';

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

/** The trainee's own certificates. Opening one gives a page they can print or save as PDF. */
export function MyCertificates() {
  const [rows, setRows] = useState<MyCertificate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try { setRows((await api.get<{ certificates: MyCertificate[] }>('/certificates/mine')).certificates); }
      catch (e) { setError((e as ApiError).message); }
    })();
  }, []);

  if (error) return <p className="auth__error">{error}</p>;
  if (!rows) return <p className="muted">Loading…</p>;

  return (
    <>
      <h1 className="h1">My certificates</h1>
      <p className="notice">Program certificates and preserved transcripts are in <Link to="/graduations">My graduation records</Link>. Course certificates appear below.</p>

      {rows.length === 0 && (
        <p className="notice">
          You have no certificates yet. Finishing a course that awards one puts it here.
        </p>
      )}

      <ul className="certs">
        {rows.map((c) => (
          <li key={c.publicId} className={`certs__row is-${c.standing}`}>
            <div>
              <strong>{c.courseName}</strong>
              <div className="muted small">
                Issued {day(c.issuedAt)}
                {c.expiresAt ? ` · valid until ${day(c.expiresAt)}` : ' · does not expire'}
                {c.score != null ? ` · ${Math.round(c.score)}%` : ''}
              </div>
              <div className="muted small mono">{c.publicId}</div>
            </div>
            <div className="row">
              {c.standing === 'valid' && <span className="pill pill--completed">valid</span>}
              {c.standing === 'expired' && <span className="pill pill--incomplete">expired</span>}
              {c.standing === 'revoked' && <span className="pill pill--failed">revoked</span>}
              <a className="btn" href={certificateUrl(c.publicId)} target="_blank" rel="noreferrer">
                Open
              </a>
              {c.asFile && (
                <a
                  className="btn btn--primary"
                  href={`${certificatePdfUrl(c.publicId)}?download=1`}
                >
                  Download PDF
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>

      {rows.length > 0 && (
        <p className="muted small">
          Download a certificate to keep it or send it on; open it to see it on screen. Anyone can confirm a
          certificate is genuine from the address printed at the bottom of it, without needing an account
          here. A certificate with no download has not been laid out for print yet — ask us and we will
          sort it out.
        </p>
      )}
    </>
  );
}
